import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Button,
  Alert,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { ConnectionDialogContent } from "./types";
import { useState } from "react";

/**
 * This dialog is used to display a message to the user when the app is offline.
 * It is used to inform the user that the app is offline and that they should check their internet connection.
 * It is also used to reload the app when the user is offline and comes back online.
 * @returns A dialog with a message to the user when the app is offline.
 */
export const ConnectionDialog = ({
  content,
}: {
  content: ConnectionDialogContent;
}) => {
  const [dismissed, setDismissed] = useState(false);
  const { t } = useTenantTranslation();

  if (dismissed) {
    return null;
  }

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="sm"
      onClose={() => {
        setDismissed(true);
      }}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            bgcolor: content.bgcolor,
            color: content.color,
          },
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {content.icon}
          <Typography variant="h5" component="div" fontWeight="bold">
            {content.title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity={content.severity} sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            {content.message}
          </Typography>
          <Typography variant="body2">{content.subtitle}</Typography>
        </Alert>

        <Box display="flex" justifyContent="center" gap={1} mt={2}>
          <Button
            sx={{
              bgcolor: content.buttonBgColor,
              color: content.buttonTextColor,
              "&:hover": {
                bgcolor: content.buttonBgColor,
              },
            }}
            onClick={() => {
              setDismissed(true);
            }}
          >
            {t("dismiss")}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              window.location.reload();
            }}
            sx={{
              bgcolor: content.buttonBgColor,
              color: content.buttonTextColor,
              "&:hover": {
                bgcolor: content.buttonBgColor,
              },
            }}
          >
            {t("tryAgain")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
